import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Save, Quote } from 'lucide-react';
import axios from 'axios';
import AdminLayout from './AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TestimonialsAdmin = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const emptyTestimonial = { quote: '', author: '', location: '', order: 0, active: true };

  useEffect(() => { fetchTestimonials(); }, []);

  const fetchTestimonials = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/testimonials`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTestimonials(response.data);
    } catch (err) {
      console.error('Failed to fetch testimonials', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingTestimonial.id) {
        await axios.put(`${API}/admin/testimonials/${editingTestimonial.id}`, editingTestimonial, { headers });
      } else {
        await axios.post(`${API}/admin/testimonials`, editingTestimonial, { headers });
      }
      
      setShowModal(false);
      setEditingTestimonial(null);
      fetchTestimonials();
    } catch (err) {
      alert('Failed to save testimonial: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTestimonials();
    } catch (err) {
      alert('Failed to delete testimonial');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="testimonials-admin-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 uppercase">Testimonials</h1>
            <p className="text-slate-500 mt-1">Manage customer testimonials</p>
          </div>
          <button
            onClick={() => { setEditingTestimonial({ ...emptyTestimonial, order: testimonials.length }); setShowModal(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="add-testimonial-btn"
          >
            <Plus className="w-5 h-5" />
            Add Testimonial
          </button>
        </div>

        {/* Testimonials List */}
        <div className="space-y-4">
          {testimonials.map((testimonial, index) => (
            <div key={testimonial.id} className="bg-white border-2 border-slate-100 p-6" data-testid={`testimonial-item-${testimonial.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <Quote className="w-5 h-5 text-orange-500" />
                    <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5">#{index + 1}</span>
                    <span className={`text-xs px-2 py-0.5 ${testimonial.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {testimonial.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-slate-600 leading-relaxed line-clamp-3 mb-3">"{testimonial.quote}"</p>
                  <p className="font-semibold text-slate-900">— {testimonial.author}</p>
                  {testimonial.location && (
                    <p className="text-sm text-orange-500">{testimonial.location}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setEditingTestimonial(testimonial); setShowModal(true); }}
                    className="p-2 text-slate-400 hover:text-orange-500"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(testimonial.id)}
                    className="p-2 text-slate-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {testimonials.length === 0 && (
          <div className="bg-white border-2 border-slate-100 p-12 text-center">
            <p className="text-slate-500">No testimonials yet. Add your first one!</p>
          </div>
        )}

        {/* Edit Modal */}
        {showModal && editingTestimonial && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="border-b border-slate-100 p-6 flex items-center justify-between">
                <h2 className="font-heading text-2xl font-bold text-slate-900 uppercase">
                  {editingTestimonial.id ? 'Edit Testimonial' : 'Add Testimonial'}
                </h2>
                <button onClick={() => { setShowModal(false); setEditingTestimonial(null); }} className="p-2 text-slate-400 hover:text-slate-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block font-heading text-xs uppercase tracking-wider text-slate-500 mb-2">Quote *</label>
                  <textarea
                    value={editingTestimonial.quote}
                    onChange={(e) => setEditingTestimonial({ ...editingTestimonial, quote: e.target.value })}
                    rows={6}
                    className="w-full px-4 py-3 border-2 border-slate-200 bg-slate-50 focus:border-orange-500"
                    placeholder="Enter the testimonial quote..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-heading text-xs uppercase tracking-wider text-slate-500 mb-2">Author Name *</label>
                    <input
                      type="text"
                      value={editingTestimonial.author}
                      onChange={(e) => setEditingTestimonial({ ...editingTestimonial, author: e.target.value })}
                      className="w-full h-12 px-4 border-2 border-slate-200 bg-slate-50 focus:border-orange-500"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block font-heading text-xs uppercase tracking-wider text-slate-500 mb-2">Location / Title</label>
                    <input
                      type="text"
                      value={editingTestimonial.location || ''}
                      onChange={(e) => setEditingTestimonial({ ...editingTestimonial, location: e.target.value })}
                      className="w-full h-12 px-4 border-2 border-slate-200 bg-slate-50 focus:border-orange-500"
                      placeholder="Fairbanks, AK"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={editingTestimonial.active}
                    onChange={(e) => setEditingTestimonial({ ...editingTestimonial, active: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <label className="text-slate-700">Active (visible on website)</label>
                </div>
              </div>

              <div className="border-t border-slate-100 p-6 flex justify-end gap-4">
                <button onClick={() => { setShowModal(false); setEditingTestimonial(null); }} className="px-6 py-3 text-slate-600 hover:bg-slate-100">
                  Cancel
                </button>
                <button onClick={handleSave} className="btn-primary flex items-center gap-2">
                  <Save className="w-5 h-5" />
                  Save Testimonial
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default TestimonialsAdmin;
